import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { usePhoneCallTracking } from "@/hooks/usePhoneCallTracking";
import Index from "./pages/Index";

// Lazy load non-critical routes
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const Memberships = lazy(() => import("./pages/Memberships"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const SmsTerms = lazy(() => import("./pages/SmsTerms"));
const NotFound = lazy(() => import("./pages/NotFound"));
// Blog
const BlogPage = lazy(() => import("./pages/Blog"));
const PlumberGulfShoresGuide = lazy(() => import("./pages/blog/PlumberGulfShoresGuide"));
// City pages
const GulfShoresPlumber = lazy(() => import("./pages/cities/GulfShoresPlumber"));
const OrangeBeachPlumber = lazy(() => import("./pages/cities/OrangeBeachPlumber"));
const FairhopePlumber = lazy(() => import("./pages/cities/FairhopePlumber"));
const DaphnePlumber = lazy(() => import("./pages/cities/DaphnePlumber"));
const FoleyPlumber = lazy(() => import("./pages/cities/FoleyPlumber"));
const SpanishFortPlumber = lazy(() => import("./pages/cities/SpanishFortPlumber"));
const RobertsdalePlumber = lazy(() => import("./pages/cities/RobertsdalesPlumber"));
const LoxleyPlumber = lazy(() => import("./pages/cities/LoxleyPlumber"));
const SummerdalePlumber = lazy(() => import("./pages/cities/SummerdalePlumber"));
const BayMinettePlumber = lazy(() => import("./pages/cities/BayMinettePlumber"));
const ElbertaPlumber = lazy(() => import("./pages/cities/ElbertaPlumber"));
const PerdidoBeachPlumber = lazy(() => import("./pages/cities/PerdidoBeachPlumber"));
const MagnoliaSpingsPlumber = lazy(() => import("./pages/cities/MagnoliaSpingsPlumber"));
// Service + City combo pages
const FoleyWaterHeater = lazy(() => import("./pages/service-city/FoleyWaterHeater"));
const FoleyDrainCleaning = lazy(() => import("./pages/service-city/FoleyDrainCleaning"));
const FairhopeDrainCleaning = lazy(() => import("./pages/service-city/FairhopeDrainCleaning"));
const DaphneWaterHeater = lazy(() => import("./pages/service-city/DaphneWaterHeater"));
const DaphneDrainCleaning = lazy(() => import("./pages/service-city/DaphneDrainCleaning"));
const GulfShoresDrainCleaning = lazy(() => import("./pages/service-city/GulfShoresDrainCleaning"));
const OrangeBeachWaterHeater = lazy(() => import("./pages/service-city/OrangeBeachWaterHeater"));

const queryClient = new QueryClient();

const App = () => {
  usePhoneCallTracking();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServicePage />} />
              <Route path="/service-areas" element={<ServiceAreas />} />
              <Route path="/memberships" element={<Memberships />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/sms-terms" element={<SmsTerms />} />
              {/* Blog */}
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/plumber-gulf-shores-guide" element={<PlumberGulfShoresGuide />} />
              {/* City landing pages for local SEO */}
              <Route path="/gulf-shores-plumber" element={<GulfShoresPlumber />} />
              <Route path="/orange-beach-plumber" element={<OrangeBeachPlumber />} />
              <Route path="/fairhope-plumber" element={<FairhopePlumber />} />
              <Route path="/daphne-plumber" element={<DaphnePlumber />} />
              <Route path="/foley-plumber" element={<FoleyPlumber />} />
              <Route path="/spanish-fort-plumber" element={<SpanishFortPlumber />} />
              <Route path="/robertsdale-plumber" element={<RobertsdalePlumber />} />
              <Route path="/loxley-plumber" element={<LoxleyPlumber />} />
              <Route path="/summerdale-plumber" element={<SummerdalePlumber />} />
              <Route path="/bay-minette-plumber" element={<BayMinettePlumber />} />
              <Route path="/elberta-plumber" element={<ElbertaPlumber />} />
              <Route path="/perdido-beach-plumber" element={<PerdidoBeachPlumber />} />
              <Route path="/magnolia-springs-plumber" element={<MagnoliaSpingsPlumber />} />
              {/* Service + City combo pages */}
              <Route path="/foley-water-heater" element={<FoleyWaterHeater />} />
              <Route path="/foley-drain-cleaning" element={<FoleyDrainCleaning />} />
              <Route path="/fairhope-drain-cleaning" element={<FairhopeDrainCleaning />} />
              <Route path="/daphne-water-heater" element={<DaphneWaterHeater />} />
              <Route path="/daphne-drain-cleaning" element={<DaphneDrainCleaning />} />
              <Route path="/gulf-shores-drain-cleaning" element={<GulfShoresDrainCleaning />} />
              <Route path="/orange-beach-water-heater" element={<OrangeBeachWaterHeater />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;